import {
  type StateCreator,
  type StoreApi,
  createStore as zustandCreateStore,
} from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

/**
 * Opt-in localStorage persistence for a store (FRONT-1187).
 *
 * Lives here rather than at the call site because composing zustand's
 * middleware is exactly the kind of type wrangling that shouldn't be repeated
 * in every store — the chain is built once, and a store just declares what it
 * wants kept.
 */
export interface PersistConfig<T> {
  /** localStorage key. Convention: `eddy.<area>` (see `eddy.sidebar.layout`). */
  key: string;
  /**
   * Bumped when the persisted shape changes incompatibly; a stored value with a
   * different version is discarded rather than merged. Defaults to 1.
   */
  version?: number;
  /**
   * Which slice to write. **Required, not optional** — persisting a whole UI
   * store by default is how you end up restoring a modal that was open when the
   * tab closed. Each store names the fields that are genuinely preferences.
   */
  partialize: (state: T) => Partial<T>;
}

/**
 * localStorage when it exists, an inert stand-in when it doesn't.
 *
 * The frontend test env is plain node with no DOM, and a Safari private window
 * throws on access rather than returning null. zustand would otherwise log a
 * warning per store and fall back anyway, so absorbing it here keeps test
 * output clean and makes "no persistence available" a non-event.
 */
function storageOrNoop(): Storage {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* access itself can throw — treat as unavailable */
  }
  const noop: Storage = {
    length: 0,
    clear: () => {},
    getItem: () => null,
    key: () => null,
    removeItem: () => {},
    setItem: () => {},
  };
  return noop;
}

/**
 * Merge a stored value back in, taking ONLY the fields the store persists.
 *
 * `partialize` governs only what gets WRITTEN. zustand's default merge
 * shallow-spreads the entire stored object over the initial state, so a stored
 * key we never intended to keep — left by an older version, or hand-edited in
 * devtools — would be restored anyway. A test for "a modal is never restored"
 * caught exactly that.
 *
 * The whitelist is derived from `partialize` itself rather than declared a
 * second time, so the written slice and the read slice cannot drift apart.
 */
function mergeOnlyPersistedKeys<T>(partialize: (state: T) => Partial<T>) {
  return (persisted: unknown, current: T): T => {
    const allowed = Object.keys(partialize(current)) as (keyof T)[];
    const incoming = (persisted ?? {}) as Partial<T>;
    const merged = { ...current };
    for (const key of allowed) {
      if (Object.prototype.hasOwnProperty.call(incoming, key)) {
        merged[key] = incoming[key] as T[keyof T];
      }
    }
    return merged;
  };
}

/**
 * Wrap an initializer in the `persist` middleware.
 *
 * The cast is why this is its own function: `persist` returns
 * `StateCreator<T, [], [['zustand/persist', unknown]]>`, which is not
 * assignable to the plain `StateCreator` the non-persisted path uses. The
 * runtime shape is identical — only the phantom mutator list differs — so the
 * cast is confined here rather than smuggled into an expression that is also
 * doing branching and config building.
 */
function withPersistence<T>(
  initializer: StateCreator<T, [], []>,
  config: PersistConfig<T>,
): StateCreator<T, [], []> {
  const options = {
    name: config.key,
    version: config.version ?? 1,
    storage: createJSONStorage(() => storageOrNoop()),
    partialize: config.partialize as (state: T) => T,
    merge: mergeOnlyPersistedKeys(config.partialize),
  };
  return persist(initializer, options) as unknown as StateCreator<T, [], []>;
}

export function createStore<T>(
  name: string,
  initializer: StateCreator<T, [], []>,
  persistConfig?: PersistConfig<T>,
): StoreApi<T> {
  const create = persistConfig ? withPersistence(initializer, persistConfig) : initializer;

  if (import.meta.env.DEV) {
    return zustandCreateStore<T>()(devtools(create, { name, enabled: true }));
  }
  return zustandCreateStore<T>()(create);
}
